// src/components/ui/loader.jsx
import React from 'react';
import { useTenant } from '../../context/TenantContext';

const SIZE_CLASSES = {
  sm: 'h-4 w-4 sm:h-6 sm:w-6',
  md: 'h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12',
  lg: 'h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20',
  xl: 'h-[75px] w-[75px]',
  '2xl': 'w-32 sm:w-40 md:w-48 lg:w-56 xl:w-64',
};

const LABEL_SIZE_CLASSES = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-sm sm:text-base md:text-lg',
  '2xl': 'text-base sm:text-lg md:text-xl',
};

// Default placeholder center SVG (cloud-style). Consumers can pass their own SVG as children.
// spinnerClassName applies only to the spinner; the image renders at its natural size.
const DefaultCenterSVG = ({ spinnerClassName = '', loaderImageUrl = '' }) => {
  // If no custom loader image, use default spinner
  if (!loaderImageUrl || loaderImageUrl.trim() === '') {
    return (
      <div className={`${spinnerClassName} flex items-center justify-center`}>
        <svg
          className="animate-spin w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  // Use custom loader image — no size constraint, renders at its natural size
  return (
    <img
      src={loaderImageUrl}
      className="max-h-[200px] object-contain"
      alt=""
      aria-hidden="true"
      role="img"
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  );
};

/**
 * Loader component used throughout the project with responsive sizing.
 * Props:
 * - fullscreen: boolean -> show a centered overlay covering the viewport
 * - label: optional string shown under the loader
 * - size: 'sm'|'md'|'lg'|'xl'|'2xl' controls responsive svg size
 * - labelColor: color class for label text (default: 'text-white')
 * - children: optional React node to render as the center SVG (overrides default)
 * - className: additional classes applied to the container
 * - overlayClassName: custom classes for fullscreen overlay
 */
export default function Loader({
  fullscreen = false,
  label = '',
  size = 'xl',
  labelColor = 'text-white',
  children = null,
  className = '',
  overlayClassName = 'backdrop-blur-sm',
  ariaLive = 'polite',
  ...props
}) {
  const { tenant } = useTenant();
  const svgSizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const labelSizeClass = LABEL_SIZE_CLASSES[size] || LABEL_SIZE_CLASSES.md
  const overlayClassName1 = 'backdrop-blur-sm'

  // Get loader image from tenant config - construct URL like banner images
  const loaderImageFilename = tenant?.tenantmasterconfig?.loaderimage || '';
  const loaderImageUrl = loaderImageFilename && loaderImageFilename.trim() !== ''
    ? `${process.env.REACT_APP_CLOUDFRONT}icons/${loaderImageFilename}`
    : '';

  // Only show translated label when language content is actually loaded
  const translationsReady = !!(tenant?.tenantlanguagecontent?.v3 && Object.keys(tenant.tenantlanguagecontent.v3).length > 0);

  const content = (
    <div className={`flex flex-col items-center justify-center gap-2 sm:gap-3 md:gap-4 ${className}`} {...props}>
      <div className="flex items-center justify-center">
        {/* center SVG: use provided children (custom SVG) else placeholder */}
        {children ? (
          <span className={svgSizeClass} aria-hidden>
            {children}
          </span>
        ) : (
          <DefaultCenterSVG spinnerClassName={svgSizeClass} loaderImageUrl={loaderImageUrl} />
        )}
      </div>

      {label && translationsReady && (
        <div className={`${labelColor} ${labelSizeClass} font-semibold text-center px-4`}>
          {label}
        </div>
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center ${overlayClassName1}`}
        aria-live={ariaLive}
        aria-label={label || 'Loading'}
        role="status"
      >
        {content}
      </div>
    );
  }

  // Inline loader (no overlay)
  return (
    <div aria-live={ariaLive} role="status">
      {content}
    </div>
  );
}

export { DefaultCenterSVG };
